import React, { useEffect, useState } from "react";

import Richtext from "@dip/helsecore/src/components/shared/Wrappers/Richtext";
import { Alert } from "@hdir/ui/alert";
import { Link } from "@hdir/ui/link";
import SideMenuWithAnchorLinks from "../../../react4xp/shared/SideMenu/Menu/SideMenuWithAnchorLinks";

import type { LovfortolkningProps, SideMenuElement } from "./types.d";

function ShowLovfortolkning(props: LovfortolkningProps) {
  const {
    title,
    intro,
    text,
    summary,
    saksnummer,
    relevantLaws,
    alertText,
    alertTitle,
    publishFirst,
    // isEditMode is used to display props: sidemenu and text conditionally as the useEffect is not run in Content Studio editview
    isEditMode
  } = props;

  const [textParsed, setTextParsed] = useState("");
  const [sidemenu, setSidemenu] = useState<SideMenuElement[]>([]);

  const idsOnPage: string[] = [];

  // Convert title to lowercase and join words with hyphens to create a valid ID. Make sure ids are unique
  function generateId(title: string): string {
    // Convert to lowercase and replace unwanted characters, keeping æøå
    let id = title
      .toLowerCase()
      .replace(/[^a-z0-9æøå-]+/gi, "-")
      .replace(/^-+|-+$/g, "");

    // Ensure unique id
    if (idsOnPage.indexOf(id) === -1) {
      idsOnPage.push(id);
    } else {
      id = `${id}-${idsOnPage.length}`;
      idsOnPage.push(id);
    }

    return id;
  }

  // Parse html from prop text. Find h2-tags, append id and build the sidemenu. Run on onLoad
  useEffect(() => {
    const parser = new DOMParser();
    const textDoc = parser.parseFromString(text, "text/html");

    const textHeadings = Array.from(textDoc.querySelectorAll("h2"));
    const menu: { title: string; link: string }[] = [];

    // Add Summary as the first element if present
    if (summary) menu.push({ title: "Sammendrag", link: "#sammendrag" });

    textHeadings.forEach((h2) => {
      const title = h2.textContent?.trim() || "";
      const id = generateId(title);
      h2.setAttribute("id", id);
      menu.push({ title, link: `#${id}` });
    });

    setTextParsed(textDoc.body.innerHTML);
    setSidemenu(menu);
  }, []);

  return (
    <section className="mt-xxxl">
      <div className="bg-surface-neutral-grey pt-xl pb-xxl">
        <div className="px-md tablet:mx-auto w-[95vw] max-w-[1150px]">
          <div className="max-w-text-width">
            <h1>{title}</h1>
            <p className="text-xl py-lg">{intro}</p>
            <div className="flex flex-row flex-wrap text-sm text-neutral-subtle pb-lg">
              { saksnummer && (
                <p className="pr-sm">
                  Saksnummer:
                  {" "}
                  {saksnummer}
                </p>
              )}
              { publishFirst && (
                <p className="pl-sm" style={{ borderLeft: "solid var(--design-border-sm) var(--design-color-text-neutral-subtle)" }}>
                  {publishFirst}
                </p>
              )}
            </div>

            { (relevantLaws && relevantLaws.length > 0) && (
              <>
                <h2 className="text-lg ">Relevante lover og forskrifter</h2>
                <div className="flex flex-row flex-wrap mt-sm gap-x-sm gap-y-xxs">
                  { relevantLaws.map((law, index) => {
                    const isLast = index === relevantLaws.length - 1;
                    return (
                      <Link
                        className="pr-sm"
                        // Dont apply style to last element
                        style={{
                          borderRight: !isLast
                            ? "solid var(--design-border-sm) var(--design-color-text-action-default)"
                            : undefined,
                          borderRadius: "0px"
                        }}
                        key={law.link}
                        href={law.link}
                        target="_blank"
                      >
                        {law.title}
                      </Link>
                    );
                  })}
                </div>
              </>
            )}
          </div>

        </div>
      </div>

      <div className="px-md tablet:mx-auto w-[95vw] max-w-[1150px]">
        { (alertTitle || alertText) && (
          <Alert className="mt-xxl" title={alertTitle} variant="info" fullWidth>{alertText}</Alert>
        )}
        <div className="flex nt-flex-col tablet:nt-flex-row mt-xxl gap-xl">
          <div className="basis-1/3 mb-auto tablet:sticky top-4">
            <SideMenuWithAnchorLinks data={isEditMode ? { title: "Sidemenyen vises i forhåndsvisning", link: "" } : sidemenu} />
          </div>
          <div className="basis-2/3 flex flex-col gap-lg">
            { summary && (
              <div className="bg-surface-neutral-subtle border border-solid rounded-md border-neutral-subtle pt-md px-xl">
                <h2 id="sammendrag">Sammendrag</h2>
                <Richtext className="mt-md t-body-text" text={summary} />
              </div>
            )}
            { (textParsed || text) && (
              <Richtext className="t-body-text" text={isEditMode ? text : textParsed} />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function(props: LovfortolkningProps) {
  return <ShowLovfortolkning {...props} />;
}
